import { useCallback } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import backend from '../../../lib/backend';
import { UploadProgressCallback } from '../../../lib/backend/requests/pushFileToSignedURL';
import { PosthogFeatureFlags } from '../../../lib/env/posthogFeatureFlags';
import { DocumentUploadProgressMap, documentUploadProgressMap, DocumentUploadProgressState } from '../../../state/atoms/documentUpload';
import documentsSubmitForm from '../../../state/forms/eslForms/documentsSubmitForm';
import { ApplicationAttachment } from '../../../types/applicationAttachment';
import { DocumentUploadValidationError } from '../../../types/errors/validation';
import {
  SponsorshipApplicationBase,
  SponsorshipApplicationFiles,
} from '../../../types/sponsorshipApplication';

import { useFeatureFlagEnabled } from 'posthog-js/react';

/**
 * Interface for the return type of the useDocumentsForm hook
 */
interface UseDocumentsForm {
  validateForm: () => void;
  uploadProgressMap: DocumentUploadProgressMap;
  uploadDocument: (doc: ApplicationAttachment, file: File) => Promise<SponsorshipApplicationFiles>;
}

/**
 * Custom hook to manage the state and submission logic for the documents form.
 *
 * @param sponsorshipApplication - The sponsorship application data
 * @returns An object containing the submitForm handler function
 */
const useDocumentsForm = (
  sponsorshipApplication: SponsorshipApplicationBase | null,
): UseDocumentsForm => {
  // Direct Upload Feature Flag
  const isDirectFileUploadEnabled = useFeatureFlagEnabled(PosthogFeatureFlags.DFU);
  const documentFiles = useRecoilValue(documentsSubmitForm.fields.documentFiles.valueState);
  const [ uploadProgressMap, setUploadProgressMap ] = useRecoilState(documentUploadProgressMap);

  const validateForm = useCallback(() => {
    const requiredDocuments = sponsorshipApplication?.opportunity_required_documents;

    if (requiredDocuments && requiredDocuments.length > 0) {
      for (const attachment of requiredDocuments) {
        const file = documentFiles[attachment.id];

        if (!file) {
          throw new DocumentUploadValidationError(`${attachment.display_name} is required.`, attachment);
        }
      }
    }
  }, [ documentFiles, sponsorshipApplication?.opportunity_required_documents ]);

  const updateUploadProgressMap = useCallback((docId: string, uploadProgress: DocumentUploadProgressState) => {
    return setUploadProgressMap((prevUploadProgressMap) => ({
      ...prevUploadProgressMap,
      [docId]: uploadProgress,
    }));
  }, [setUploadProgressMap]);

  const handleUploadProgress = useCallback<UploadProgressCallback>((progressEvent, docId) => {
    const uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);

    updateUploadProgressMap(
      docId,
      {
        uploading: true,
        uploaded: uploadProgress == 100,
        uploadProgress,
      },
    );
  }, [updateUploadProgressMap]);

  /**
   * Uploads the file directly to GCS
   * @param attachment Attachment being uploaded
   * @param file File being uploaded
   * @returns Uploaded file
   */
  const directFileUpload = useCallback(async (attachment: ApplicationAttachment, file: File): Promise<SponsorshipApplicationFiles> => {
    const attachmentId = attachment.id;
    const docType = attachment.document_type.type;
    const sponsorshipApplicationId = sponsorshipApplication?.id as string;

    try {
      // Step 0: Set the upload progress state
      updateUploadProgressMap(attachmentId, { uploading: true, uploaded: false, uploadProgress: 0 });
      console.debug(`Direct uploading file ⚡: ${file.name}, Size: ${file.size} bytes`);

      // Step 1: Generate Signed URL
      const { signed_url, file_path } = await backend.getUploadSignedUrl(sponsorshipApplicationId, attachmentId, file.name);

      // Step 2: Push File to Signed URL
      await backend.pushFileToSignedUrl(signed_url, file, attachmentId, handleUploadProgress);

      // Step 3: Confirm File Upload
      const confirmedFile = await backend.confirmFileUpload(sponsorshipApplicationId, file_path, attachmentId);

      // Step 4: Set the uploaded state
      updateUploadProgressMap(attachmentId, { uploading: false, uploaded: true, uploadProgress: 100 });

      return { [attachmentId]: confirmedFile };
    } catch (error) {
      console.error(`Error uploading file for type ${docType}:`, error);
      updateUploadProgressMap(attachmentId, { uploading: false, uploaded: false, uploadProgress: 0 });

      throw new DocumentUploadValidationError(
        `Failed to upload file of type ${docType}. Please try again.`,
        attachment,
      );
    }
  }, [ handleUploadProgress, sponsorshipApplication?.id, updateUploadProgressMap ]);

  /**
   * Uploads the file using the legacy submit API
   * @param attachment Attachment being uploaded
   * @param file File being uploaded
   * @returns Mapping of attachment ID to the uploaded file
   */
  const legacyFileUpload = useCallback(async (attachment: ApplicationAttachment, file: File) => {
    const attachmentId = attachment.id;

    updateUploadProgressMap(attachmentId, { uploading: true, uploaded: false, uploadProgress: 0 });
    const formData = new FormData();
    formData.append(attachmentId, file);

    try {
      const { data: sponsorshipApplicationFiles } = await backend.submitFiles(sponsorshipApplication?.id as string, formData);
      console.log('File upload successful:', sponsorshipApplicationFiles);
      updateUploadProgressMap(attachmentId, { uploading: false, uploaded: true, uploadProgress: 100 });

      return sponsorshipApplicationFiles;
    } catch (error) {
      console.error('File upload failed:', error);
      updateUploadProgressMap(attachmentId, { uploading: false, uploaded: false, uploadProgress: 0 });

      throw error;
    }
  }, [ updateUploadProgressMap, sponsorshipApplication?.id ]);

  /**
   * Performs the upload based on the enabled FF
   * @param attachment Attachment being uploaded
   * @param file File being uploaded
   * @returns Mapping of attachment ID to the uploaded file
   */
  const uploadDocument = useCallback(async (attachment: ApplicationAttachment, file: File) => {
    if (isDirectFileUploadEnabled) {
      return directFileUpload(attachment, file);
    }

    return legacyFileUpload(attachment, file);
  }, [ isDirectFileUploadEnabled, directFileUpload, legacyFileUpload ]);

  return { validateForm, uploadProgressMap, uploadDocument };
};

export default useDocumentsForm;
